/**
 * AI の利用箇所 1 件分の設定を変えるフォーム
 *
 * 変えられるのはモデルの順番・無料枠を数えるか・有料のキーに切り替えるか・組み合わせだけ（プロンプトは読み取り専用）。
 * 変えた内容があるときだけ保存でき、「変更を破棄」で開いたときの値に戻せる。
 */

import { useState } from 'react'
import { toast } from 'sonner'

import { resetAiUsage, updateAiUsage } from '@/api/services/ai-usages'
import type { AiModelsResponse, AiUsageItem, AiUsageSetting } from '@/api/types/ai-usages'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { apiErrorMessage } from '@/lib/api-error'

import { AiModelListEditor } from './ai-model-list-editor'

type Props = {
  item: AiUsageItem
  /** 選べるモデルの一覧。null は読み込み中 */
  catalog: AiModelsResponse | null
  onSaved: (item: AiUsageItem) => void
}

const toSetting = (item: AiUsageItem): AiUsageSetting => ({
  models: item.models.map((model) => model.name),
  quota_guard: item.quota_guard,
  allow_paid: item.allow_paid,
  paid_models: item.paid_models,
  profiles: item.profiles,
})

export const AiUsageEditForm = ({ item, catalog, onSaved }: Props) => {
  const saved = toSetting(item)
  const [draft, setDraft] = useState<AiUsageSetting>(saved)
  const dirty = JSON.stringify(draft) !== JSON.stringify(saved)
  const [busy, setBusy] = useState(false)

  const run = async (action: () => Promise<AiUsageItem>, done: string, failed: string) => {
    setBusy(true)
    try {
      onSaved(await action())
      toast.success(done)
    } catch (error) {
      toast.error(apiErrorMessage(error, failed))
    } finally {
      setBusy(false)
    }
  }

  const switchId = `${item.key}-switch`

  return (
    <div className="space-y-5">
      <div>
        <p className="text-xs font-medium text-muted-foreground">モデル（前から順に試す）</p>
        <div className="mt-1">
          <AiModelListEditor
            models={draft.models}
            catalog={catalog}
            minCount={1}
            onChange={(models) => setDraft({ ...draft, models })}
          />
        </div>
        <p className="mt-1 text-xs text-muted-foreground">初期値: {item.defaults.models.join(' → ')}</p>
      </div>

      {draft.allow_paid && Object.keys(item.profiles).length > 0 && (
        <p className="text-xs text-muted-foreground">
          組み合わせ（{Object.keys(item.profiles).join(' / ')}）で読むため、有料のキーでも同じモデルを使います。
        </p>
      )}
      {draft.allow_paid && Object.keys(item.profiles).length === 0 && (
        <div>
          <p className="text-xs font-medium text-muted-foreground">有料のキーで使うモデル（前から順に試す）</p>
          <div className="mt-1">
            <AiModelListEditor
              models={draft.paid_models}
              catalog={catalog}
              minCount={0}
              onChange={(paid_models) => setDraft({ ...draft, paid_models })}
            />
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            空のときは無料のキーと同じモデルを使います。
            {item.profile_min_models > 1 &&
              '組み合わせ（daily / bulk）で呼ぶときは、有料のキーでも同じモデルを使います。'}
          </p>
        </div>
      )}

      {Object.entries(draft.profiles).map(([name, models]) => (
        <div key={name}>
          <p className="text-xs font-medium text-muted-foreground">
            {name} のとき（{item.profile_min_models} 個以上）
          </p>
          <div className="mt-1">
            <AiModelListEditor
              models={models}
              catalog={catalog}
              minCount={item.profile_min_models}
              onChange={(next) => setDraft({ ...draft, profiles: { ...draft.profiles, [name]: next } })}
            />
          </div>
        </div>
      ))}

      <div className="space-y-3">
        <label htmlFor={`${switchId}-quota`} className="flex items-center justify-between gap-3">
          <span>
            <span className="block text-sm">無料枠を数える</span>
            <span className="block text-xs text-muted-foreground">
              無料のキーで今日の上限に達したモデルは飛ばし、毎分の上限では待つ
            </span>
          </span>
          <Switch
            id={`${switchId}-quota`}
            checked={draft.quota_guard}
            onCheckedChange={(quota_guard) => setDraft({ ...draft, quota_guard })}
          />
        </label>
        <label htmlFor={`${switchId}-paid`} className="flex items-center justify-between gap-3">
          <span>
            <span className="block text-sm">有料のキーに切り替える</span>
            <span className="block text-xs text-muted-foreground">
              無料のキーで答えられなかったとき、同じモデルを有料のキーで試す
            </span>
          </span>
          <Switch
            id={`${switchId}-paid`}
            checked={draft.allow_paid}
            onCheckedChange={(allow_paid) => setDraft({ ...draft, allow_paid })}
          />
        </label>
      </div>

      <div className="flex flex-wrap justify-end gap-2">
        {item.customized && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={busy}
            onClick={() => void run(() => resetAiUsage(item.key), '初期値に戻しました', '初期値に戻せませんでした')}
          >
            初期値に戻す
          </Button>
        )}
        <Button type="button" variant="outline" size="sm" disabled={busy || !dirty} onClick={() => setDraft(saved)}>
          変更を破棄
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={busy || !dirty}
          onClick={() => void run(() => updateAiUsage(item.key, draft), '保存しました', '保存できませんでした')}
        >
          保存
        </Button>
      </div>
    </div>
  )
}
